import {
  Component,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DprSubActivityGridData } from '@dpr-details/models/grid';
import { TextDisplayComponent, TextEditComponent } from '@grid/components';
import { GridAlignment } from '@grid/enums';
import { GridApi, GridColumn, GridOptions } from '@grid/models';
import {
  ConfirmDialogComponent,
  TimeDisplayComponent,
  TimeEditComponent,
} from '@shared/components';
import { StaticMessage } from '@shared/enums';
import { GridBaseComponent } from '@shared/models/component';
import {
  compareTime,
  Constants,
  convertTimeToDate,
  getStaticMessage,
} from '@shared/utils';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';

@Component({
  selector: 'app-dpr-edit-activity-subactivities-grid',
  templateUrl: './dpr-edit-activity-subactivities-grid.component.html',
  styleUrls: ['./dpr-edit-activity-subactivities-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprEditActivitySubActivitiesGridComponent
  extends GridBaseComponent<DprSubActivityGridData>
  implements OnChanges
{
  gridEntityName = $localize`:@@dprDetails.dprEditActivityModal.dprEditActivitySubActivitiesGrid.txtSubActivity:Sub Activity`;
  activityNameField = $localize`:@@dprDetails.dprEditActivityModal.dprEditActivitySubActivitiesGrid.txtActivityName:Activity`;
  startField = $localize`:@@dprDetails.dprEditActivityModal.dprEditActivitySubActivitiesGrid.txtStart:Start`;
  finishField = $localize`:@@dprDetails.dprEditActivityModal.dprEditActivitySubActivitiesGrid.txtFinish:Finish`;

  constructor(private dialog: MatDialog) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  validateChanges(): boolean {
    const changes = this.gridComponent?.api?.getChanges();
    const entities = [...changes.addedList, ...changes.updatedList];

    for (const entity of entities) {
      const errorMessages = [];

      if (String.isNullOrWhiteSpace(entity.name)) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.activityNameField
          )
        );
      }

      if (!entity.start) {
        errorMessages.push(
          getStaticMessage(StaticMessage.FieldMandatoryError, this.startField)
        );
      }

      if (!entity.finish) {
        errorMessages.push(
          getStaticMessage(StaticMessage.FieldMandatoryError, this.finishField)
        );
      }

      if (
        entity.start &&
        entity.finish &&
        compareTime(
          convertTimeToDate(entity.start),
          convertTimeToDate(entity.finish)
        ) > 0
      ) {
        errorMessages.push(
          $localize`:@@dprDetails.dprEditActivityModal.dprEditActivitySubActivitiesGrid.errStartMustBeBeforeFinish:- Start cannot be greater than Finish. Please fix values before saving.`
        );
      }

      if (errorMessages.length) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  isRowEmpty(item: DprSubActivityGridData): boolean {
    return !(
      typeof item.id === 'number' ||
      !String.isNullOrWhiteSpace(item.name) ||
      !String.isNullOrWhiteSpace(item.start) ||
      !String.isNullOrWhiteSpace(item.finish)
    );
  }

  protected getGridOptions(): GridOptions<DprSubActivityGridData> {
    return {
      labeling: {
        ...GridDefaultLabels,
      },
      addConfig: {
        allowAdding: this.canEdit,
        buttonPosition: 'header',
        value: {
          name: '',
        },
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: this.canEdit,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: true,
        allowSelection: true,
        isEmpty: this.isRowEmpty.bind(this),
      },
      selectionConfig: {
        allowSelection: this.canEdit,
      },
      headerConfig: {
        enableStickyDirective: false,
      },
      events: {
        onGridRegistered: (gridApi: GridApi<DprSubActivityGridData>) => {
          this.gridRegistered.emit(gridApi);
        },
      },
    };
  }

  protected getGridColumns(): GridColumn<DprSubActivityGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: this.gridEntityName,
        },
        display: { type: TextDisplayComponent },
        edit: {
          type: TextEditComponent,
          config: { options: { maxLength: 200 } },
        },
        minWidth: 200,
      },
      {
        columnDef: 'start',
        header: {
          name: this.startField,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        display: { type: TimeDisplayComponent },
        edit: {
          type: TimeEditComponent,
          config: { options: { maxLength: 200 } },
        },
        width: 90,
      },
      {
        columnDef: 'finish',
        header: {
          name: this.finishField,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        display: { type: TimeDisplayComponent },
        edit: {
          type: TimeEditComponent,
          config: { options: { maxLength: 200 } },
        },
        width: 90,
      },
      GridColumns.delete,
    ];
  }
}
